/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { apiClient } from '@lib/api-client';
import { useQuery } from '@tanstack/react-query';

// utils
import {
	convertProduction,
	formatDate,
	parseFcApplicable,
} from '@utils/functions';

// constants
import { NOT_APPLICABLE } from '@config/constants/common';

// lodash
import { ROLES } from '@config/constants/roles';
import { TMesurabilityProject } from '@features/resources/measurabilities/types';
import { TParamsGetApi } from '@global-types/index';
import { useAuthorization } from '@lib/authorization';
import {
	getConvertionFactor,
	getNumericProduction,
	getNumericTokenEquivalent,
	parseTokenEquivalentToTCO2E,
} from '@utils/productions';
import isNil from 'lodash/isNil';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */
export type TApiResponse = {
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
	rows: TMesurabilityProject[];
};

export type TProductionPopulated = {
	meterPoint: {
		site: string;
		serialNumber: string;
		production: {
			minValue: number;
			maxValue: number;
		};
		organization: string;
		entity: string;
		project: string;
		unit: string;
		location: {
			countryCode: string;
			place: string;
		};
		createdAt: string;
		updatedAt: string;
		id: string;
	};
	startDate: string;
	quantity: number;
	proof: string;
	verified: boolean;
	creationMethod: string;
	status: string;
	fromImport: string;
	organization: {
		name: string;
		owner: string;
		verified: boolean;
		location: {
			countryCode: string;
			place: string;
		};
		createdAt: string;
		updatedAt: string;
		id: string;
	};
	entity: {
		organization: string;
		name: string;
		verified: boolean;
		obfuscateIPFS: boolean;
		location: {
			countryCode: string;
			place: string;
		};
		createdAt: string;
		updatedAt: string;
		id: string;
	};
	project: {
		entity: string;
		name: string;
		unit: string;
		technology: string;
		active: boolean;
		cover: string;
		publicSpecs: any[];
		internalSpecs: any[];
		sellPrice_europertco2eq: number;
		costPrice_europertco2eq: number;
		quantity: {
			total_aco2: number;
			held_aco2: number;
			burnt_aco2: number;
			remaining_aco2: number;
			unprocessed_gco2eq: number;
		};
		organization: string;
		location: {
			countryCode: string;
			place: string;
		};
		createdAt: string;
		updatedAt: string;
		id: string;
	};
	site: {
		name: string;
		project: string;
		organization: string;
		entity: string;
		unit: string;
		location: {
			countryCode: string;
			place: string;
		};
		createdAt: string;
		updatedAt: string;
		id: string;
	};
	unit: string;
	endDate: string;
	duration_ms: number;
	quantityCheck: string;
	createdAt: string;
	updatedAt: string;
	verifiedAt: string;
	verifiedBy: {
		locale: {
			language: string;
			timezone: string;
		};
		email: string;
		createdAt: string;
		disabled: boolean;
		firstName: string;
		lastName: string;
		role: string;
		updatedAt: string;
		id: string;
	};
	conversionFactorOverride: number;
	aco2Token: {
		blockchain: string;
		transactionHash: string;
	};

	conversionFactor: {
		site: string;
		ratio: number;
		startDate: string;
		isUsed: boolean;
		organization: string;
		entity: string;
		project: string;
		unit: string;
		createdAt: string;
		updatedAt: string;
		id: string;
	};
	id: string;
};
export type TApiResponseGetProductions = {
	rows: TProductionPopulated[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
};

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getProductions = (
	params: TParamsGetApi
): Promise<TApiResponseGetProductions> => {
	const requestParams: Record<string, unknown> = {
		page: params.page,
		pageSize: params.pageSize,
		populate: [
			'project',
			'conversionFactor',
			'organization',
			'entity',
			'meterPoint',
		],
		sort: '-createdAt',
	};

	if (params.queryParams) {
		requestParams.query = params.queryParams;
	}

	return apiClient.get('/productions', {
		params: requestParams,
	});
};
export const getMeasurabilities =
	(): Promise<TApiResponse> => {
		return apiClient.get('/measurabilities', {
			params: {
				fields: ['id'],
			},
		});
	};

export const useProductions = (
	page: number,
	size: number,
	queryParams?: Record<string, any>,
	sortParams?: string
) => {
	const { checkRole } = useAuthorization();

	const isProducer = checkRole([
		ROLES.PRODUCER_ADMIN,
		ROLES.PRODUCER_STAFF,
	]);

	const { data: measurabilities } = useQuery({
		queryKey: ['all-measurabilities'],
		queryFn: getMeasurabilities,
		enabled: isProducer,
		staleTime: 0,
	});

	const idsMeasurabilities = Array.isArray(
		measurabilities?.rows
	)
		? measurabilities?.rows?.map(
				(measurability) => measurability.id
			)
		: [];

	let newQueryParams = isProducer
		? { ...queryParams, project: idsMeasurabilities }
		: queryParams;

	const { data, isFetching, isFetched } = useQuery({
		staleTime: 0,
		queryKey: [
			'productions',
			{
				page: page,
				size: size,
				queryParams: newQueryParams,
				sortParams: sortParams,
			},
		],
		queryFn: () =>
			getProductions({
				page: page,
				pageSize: size,
				queryParams: newQueryParams ?? null,
				sortParams: sortParams ?? null,
			}),
		enabled:
			isProducer && Array.isArray(measurabilities?.rows),
	});

	const updateList = (
		productions: TProductionPopulated[]
	) =>
		productions.reduce(
			(acc: any, currentItem: TProductionPopulated) => {
				// Fc applicable
				const fcApplicable = parseFcApplicable(
					currentItem.conversionFactorOverride,
					currentItem.conversionFactor,
					currentItem.unit
				);

				const convertionFactor = getConvertionFactor(
					currentItem.conversionFactorOverride,
					currentItem.conversionFactor
				);
				// Mint status
				const { status } = currentItem;
				const mintStatus = status ?? 'None';

				const modifiedItem = {
					...currentItem,
					organization: !isNil(currentItem.organization)
						? currentItem.organization.name
						: NOT_APPLICABLE,
					organizationId: !isNil(currentItem.organization)
						? currentItem.organization.id
						: NOT_APPLICABLE,
					meterPoint: !isNil(currentItem.meterPoint)
						? currentItem.meterPoint.serialNumber
						: NOT_APPLICABLE,

					entity: !isNil(currentItem.entity)
						? currentItem.entity.name
						: NOT_APPLICABLE,
					proof: !isNil(currentItem.proof)
						? currentItem.proof
						: NOT_APPLICABLE,
					projectId: !isNil(currentItem.project)
						? currentItem.project.id
						: NOT_APPLICABLE,
					siteId: !isNil(currentItem.site)
						? currentItem.site.id
						: NOT_APPLICABLE,
					project: !isNil(currentItem.project)
						? currentItem.project.name
						: NOT_APPLICABLE,
					startDate: !isNil(currentItem.startDate)
						? formatDate(
								currentItem.startDate,
								'DD/MM/YYYY'
							)
						: NOT_APPLICABLE,
					endDate: !isNil(currentItem.endDate)
						? formatDate(
								currentItem.endDate,
								'DD/MM/YYYY'
							)
						: NOT_APPLICABLE,
					createdAt: !isNil(currentItem.createdAt)
						? formatDate(
								currentItem.createdAt,
								'DD/MM/YYYY'
							)
						: NOT_APPLICABLE,

					production: convertProduction(
						currentItem.quantity,
						currentItem.unit
					),
					productionNumeric: getNumericProduction(
						currentItem.quantity,
						currentItem.unit
					),
					tokenEquivalentNumeric:
						getNumericTokenEquivalent(
							convertionFactor,
							currentItem.quantity
						),
					tokenEquivalent: parseTokenEquivalentToTCO2E(
						convertionFactor,
						currentItem.quantity
					),
					fcApplicable: fcApplicable,
					mintStatus: mintStatus,
				};

				acc.push(modifiedItem);

				return acc;
			},
			[]
		);

	return {
		data: data ? updateList(data?.rows) : [],
		isLoading: isFetching && !isFetched,
		isFetched,
		total: data?.total,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
	};
};
